import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import JiraConnectWizard from '../../jiraoauth/v21/JiraConnectWizard';
import { JiraIcon, GithubIcon } from '../../icons/brandIcons';
import { startGithubInstall } from '../../github/githubApi';
import {
  useTeamSetup,
  TeamFields,
  CurrentWorkFields,
  StandupFields,
  InfoIcon,
} from '../../workspace-teams/teamSetup';
import trooprMark from '../../../media/circular_troopr_logo.svg';
import './onboarding.css';

// Two-pane onboarding wizard. The three team-configuration steps (Team /
// Current work / Standup) are shared with the in-app "Create a team" modal via
// ../../workspace-teams/teamSetup (hook + field UIs). This file owns the
// workspace-level connect steps, the Preview, and the shell.

// Slack bot install. Reuses the legacy flow: redirect to Slack with the app's
// bot scopes, Slack returns to /slack_auth, which posts the code to /oauth ->
// addSlackTeam (fresh-install branch) and links the new Team to this workspace.
const SLACK_BOT_SCOPES =
  'channels:history,channels:join,channels:read,chat:write,commands,files:read,' +
  'groups:history,groups:read,im:history,im:write,mpim:history,usergroups:read,' +
  'users:read,users:read.email,im:read,mpim:read';
const SLACK_CLIENT_ID =
  process.env.REACT_APP_SLACK_CLIENT_ID || process.env.REACT_APP_CLIENT_ID;

const STEPS = [
  { key: 'Slack', label: 'Connect Slack' },
  { key: 'Connect', label: 'Jira & GitHub' },
  { key: 'Team', label: 'Create team' },
  { key: 'Current work', label: 'Current work' },
  { key: 'Standup', label: 'Standup' },
  { key: 'Preview', label: 'Preview' },
];

const ShieldIcon = ({ stroke, size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2l8 3v6c0 5-3.4 8.4-8 11-4.6-2.6-8-6-8-11V5l8-3z"
      stroke={stroke}
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

const OnboardingWizard = () => {
  const wId = localStorage.getItem('userCurrentWorkspaceId');
  const t = useTeamSetup(wId);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Wizard-only connect chrome.
  const [slackError, setSlackError] = useState('');
  const [showJiraWizard, setShowJiraWizard] = useState(false);
  const [githubConnecting, setGithubConnecting] = useState(false);

  const stepName = STEPS[step].key;

  // Surface results handed back via redirect (Slack/GitHub/Jira connect).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const slackErr = params.get('slack_error');
    const github = params.get('github');
    if (slackErr) {
      const msg =
        slackErr === 'slack_already_linked'
          ? 'This Slack workspace is already connected to another Troopr workspace. Disconnect it there first, or connect a different Slack workspace.'
          : 'Could not connect Slack. Please try again.';
      setSlackError(msg);
      message.error(msg, 8);
    }
    if (github === 'connected') {
      message.success('GitHub connected', 5);
      t.setGithubConnected(true);
      setStep(1);
    } else if (github === 'error') {
      message.error('Could not connect GitHub. Please try again.', 6);
      setStep(1);
    }
    const jira = params.get('jira');
    if (jira === 'connected') {
      message.success('Jira connected', 5);
      t.setJiraConnected(true);
      setStep(1);
    }
    if (slackErr || github || jira) window.history.replaceState({}, '', '/onboarding');
  }, []);

  // GitHub install (optional) — same return-to-onboarding flow as elsewhere.
  const connectGithub = () => {
    setGithubConnecting(true);
    startGithubInstall(wId, 'onboarding')
      .then((res) => {
        setGithubConnecting(false);
        if (res.data && res.data.success && res.data.url) {
          window.location.href = res.data.url;
        } else {
          message.error((res.data && res.data.message) || 'GitHub is not configured yet');
        }
      })
      .catch(() => {
        setGithubConnecting(false);
        message.error('Could not start GitHub connect');
      });
  };

  // Kick off the bot install through the existing legacy Slack OAuth flow.
  const connectSlack = () => {
    setSlackError('');
    localStorage.setItem('troopr_workspace_id_url', `/w/${wId}`);
    window.location.href =
      'https://slack.com/oauth/v2/authorize' +
      `?client_id=${SLACK_CLIENT_ID}` +
      `&scope=${encodeURIComponent(SLACK_BOT_SCOPES)}` +
      '&state=v21_onboarding';
  };

  // Persist whatever the current step produced, then advance.
  const goNext = async () => {
    if (step >= STEPS.length - 1) return finish();
    if (stepName === 'Slack' && !t.slackConnected) {
      return message.error('Connect Slack to continue');
    }
    if (stepName === 'Connect' && !t.jiraConnected) {
      return message.error('Connect Jira to continue');
    }
    try {
      setSaving(true);
      if (stepName === 'Team') {
        if (!t.teamValid) {
          setSaving(false);
          return message.error(t.name.trim() ? 'Pick a Slack channel' : 'Enter a team name');
        }
        await t.saveTeamStep();
      } else if (stepName === 'Current work') {
        if (!t.currentWorkValid) {
          setSaving(false);
          return message.error('Pick a Jira project and which issues count as current work');
        }
        await t.saveCurrentWork();
      } else if (stepName === 'Standup') {
        await t.saveStandup();
      }
      setSaving(false);
      setStep(step + 1);
    } catch (e) {
      setSaving(false);
      message.error('Could not save. Try again.');
    }
  };

  const finish = () => {
    window.location.href = `/${wId}/dashboard`;
  };

  if (!wId) {
    return (
      <div className="tpo">
        <div className="tpo__window" style={{ height: 'auto', padding: 40 }}>
          Open or create a workspace first.
        </div>
      </div>
    );
  }

  if (showJiraWizard) {
    return (
      <JiraConnectWizard wId={wId} from="onboarding" onCancel={() => setShowJiraWizard(false)} />
    );
  }

  const isLast = step >= STEPS.length - 1;
  const nextDisabled =
    (stepName === 'Slack' && !t.slackConnected) ||
    (stepName === 'Connect' && !t.jiraConnected) ||
    (stepName === 'Team' && !t.teamValid) ||
    (stepName === 'Current work' && !t.currentWorkValid);

  return (
    <div className="tpo">
      <div className="tpo__window">
        {/* left brand rail */}
        <div className="tpo__rail">
          <div className="tpo__rail-brand">
            <img src={trooprMark} alt="" className="tpo__logo-mark-img" />
            <span className="tpo__wordmark">Troopr</span>
          </div>
          <div className="tpo__rail-headline">Let's get your workspace running.</div>
          <div className="tpo__rail-sub">
            Six quick steps. I do the heavy lifting straight from your tools.
          </div>

          <div className="tpo__stepper">
            {STEPS.map((s, i) => (
              <div
                key={s.key}
                className={
                  'tpo__srow' +
                  (i === step ? ' tpo__srow--active' : '') +
                  (i < step ? ' tpo__srow--done' : '')
                }
              >
                <span className="tpo__scircle">{i < step ? '✓' : i + 1}</span>
                <span className="tpo__slabel">{s.label}</span>
              </div>
            ))}
          </div>

          <div className="tpo__rail-foot">
            <ShieldIcon stroke="rgba(255,255,255,.55)" size={16} />
            <span>Never trains on your data. Raw messages are never stored.</span>
          </div>
        </div>

        {/* right pane */}
        <div className="tpo__pane">
          <div className="tpo__pane-body">{renderStep()}</div>
          <div className="tpo__pane-foot">
            <span className="tpo__hint">{footerHint()}</span>
            <div className="tpo__foot-actions">
              {step > 0 && (
                <button className="tpo__btn-ghost" onClick={() => setStep(step - 1)}>
                  Back
                </button>
              )}
              <button
                className="tpo__btn-primary"
                disabled={nextDisabled || saving}
                onClick={goNext}
              >
                {isLast ? 'Looks great, finish setup' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  function footerHint() {
    switch (stepName) {
      case 'Slack':
        return t.slackConnected
          ? 'Slack connected — continue to Jira & GitHub'
          : 'Connect Slack to continue';
      case 'Connect':
        return t.jiraConnected
          ? 'Jira connected — GitHub is optional'
          : 'Connect Jira to continue — GitHub is optional';
      case 'Team':
        return 'Name your team and pick where its members live';
      case 'Current work':
        return 'Pick how Troopr should scope your current work';
      case 'Standup':
        return 'Skippable — turn the standup on now or later';
      case 'Preview':
        return "Assembled from signal — you didn't type any of it";
      default:
        return '';
    }
  }

  function Eyebrow({ children }) {
    return (
      <div className="tpo__eyebrow">
        <span className="tpo__eyebrow-mark" />
        {children}
      </div>
    );
  }

  function renderStep() {
    switch (stepName) {
      case 'Slack':
        return (
          <>
            <Eyebrow>Workspace setup</Eyebrow>
            <div className="tpo__title">Connect Slack for your workspace</div>
            <div className="tpo__desc">
              One connection for the whole org, set once by an admin. Every team you create
              shares it, so you'll never connect Slack again.
            </div>
            <div className={'tpo__conn' + (t.slackConnected ? ' tpo__conn--ok' : '')}>
              <span className="tpo__conn-tile tpo__conn-tile--slack">#</span>
              <div className="tpo__conn-main">
                <div className="tpo__conn-name">
                  Slack
                  {!t.slackConnected && <span className="tpo__badge">Required</span>}
                </div>
                <div className="tpo__conn-sub">
                  {t.slackConnected
                    ? 'Your workspace is linked'
                    : 'Where your team works and where Troopr delivers'}
                </div>
              </div>
              {t.slackConnected ? (
                <span className="tpo__conn-done">✓ Connected</span>
              ) : (
                <button className="tpo__conn-btn" onClick={connectSlack}>Connect</button>
              )}
            </div>
            {slackError ? (
              <div className="tpo__callout tpo__callout--red">
                <InfoIcon stroke="#c0392b" />
                <span>{slackError}</span>
              </div>
            ) : (
              <div className="tpo__callout tpo__callout--green">
                <ShieldIcon stroke="#2f9d54" />
                <span>
                  Troopr never trains models on your data and never stores raw messages. You'll
                  always see exactly what it reads.
                </span>
              </div>
            )}
          </>
        );

      case 'Connect':
        return (
          <>
            <Eyebrow>Workspace setup</Eyebrow>
            <div className="tpo__title">Connect Jira and GitHub</div>
            <div className="tpo__desc">Two more at the workspace level. Both take one click.</div>

            <div className={'tpo__conn' + (t.jiraConnected ? ' tpo__conn--ok' : '')}>
              <span className="tpo__conn-tile tpo__conn-tile--jira"><JiraIcon size={22} /></span>
              <div className="tpo__conn-main">
                <div className="tpo__conn-name">
                  Jira
                  {!t.jiraConnected && <span className="tpo__badge">Required</span>}
                </div>
                <div className="tpo__conn-sub">
                  {t.jiraConnected
                    ? 'Connected at the workspace level'
                    : 'Troopr reads your team\'s Jira work to write the standup. One workspace-level connection.'}
                </div>
              </div>
              {t.jiraConnected ? (
                <span className="tpo__conn-done">✓ Connected</span>
              ) : (
                <button className="tpo__conn-btn" onClick={() => setShowJiraWizard(true)}>
                  Connect
                </button>
              )}
            </div>

            <div className={'tpo__conn' + (t.githubConnected ? ' tpo__conn--ok' : '')} style={{ marginTop: 12 }}>
              <span className="tpo__conn-tile tpo__conn-tile--github"><GithubIcon size={22} /></span>
              <div className="tpo__conn-main">
                <div className="tpo__conn-name">
                  GitHub
                  {!t.githubConnected && <span className="tpo__badge tpo__badge--gray">Recommended</span>}
                </div>
                <div className="tpo__conn-sub">
                  {t.githubConnected
                    ? 'Connected at the workspace level'
                    : 'So the standup knows when work actually shipped, not just when it was discussed.'}
                </div>
              </div>
              {t.githubConnected ? (
                <span className="tpo__conn-done">✓ Connected</span>
              ) : (
                <button className="tpo__conn-btn" onClick={connectGithub} disabled={githubConnecting}>
                  {githubConnecting ? 'Connecting…' : 'Connect'}
                </button>
              )}
            </div>

            <div className="tpo__callout tpo__callout--blue">
              <InfoIcon />
              <span>
                This workspace connection is only for receiving events. Per-person Jira/GitHub is
                set up later so each person's updates post as them.
              </span>
            </div>
          </>
        );

      case 'Team':
        return (
          <>
            <Eyebrow>Create your team</Eyebrow>
            <div className="tpo__title">Create your team</div>
            <div className="tpo__desc">
              Slack and Jira are connected. Point Troopr at where your team works and it pulls in
              the rest.
            </div>
            <TeamFields t={t} />
          </>
        );

      case 'Current work':
        return (
          <>
            <Eyebrow>Create your team</Eyebrow>
            <div className="tpo__title">Where does this team's work live?</div>
            <div className="tpo__desc">
              Point me at the Jira project(s) — and repo(s), if you use GitHub — this team works in,
              then tell me which issues count as <b>active work</b>. The standup, reports and risk
              signals all flow from it.
            </div>
            <CurrentWorkFields t={t} />
          </>
        );

      case 'Standup':
        return (
          <>
            <Eyebrow>Set up the standup</Eyebrow>
            <div className="tpo__title">
              Turn on the automated standup
              <span className="tpo__title-pill">Recommended</span>
            </div>
            <div className="tpo__desc">
              The one I recommend first. I write each person's update from Jira, GitHub and Slack
              and post it to your channel. No one types anything.
            </div>
            <StandupFields t={t} />
          </>
        );

      case 'Preview':
        return (
          <>
            <Eyebrow>You're basically done</Eyebrow>
            <div className="tpo__title">Here's what I've set up</div>
            <div className="tpo__desc">
              This is your team's configuration. {t.standupEnabled
                ? `Your first standup posts on schedule — the engine writes each person's line from the signal below.`
                : `The automated standup is off for now; turn it on anytime from the team's settings.`}
            </div>

            <div className="tpo__group">
              <div className="tpo__grow">
                <span className="tpo__conn-tile tpo__conn-tile--slack" style={{ width: 38, height: 38, fontSize: 18 }}>#</span>
                <div className="tpo__grow-text">
                  <span className="tpo__grow-label">Team</span>
                  <span className="tpo__grow-value">
                    {t.name || '—'} · {t.members.length} member{t.members.length === 1 ? '' : 's'}
                    {t.selectedChannel ? ` · #${t.selectedChannel.name}` : ''}
                  </span>
                </div>
              </div>
              <div className="tpo__grow">
                <span className="tpo__conn-tile tpo__conn-tile--jira" style={{ width: 38, height: 38 }}><JiraIcon size={18} /></span>
                <div className="tpo__grow-text">
                  <span className="tpo__grow-label">Current work</span>
                  <span className="tpo__grow-value">{t.currentWorkLabel()}</span>
                </div>
              </div>
              <div className="tpo__grow">
                <span className="tpo__conn-tile tpo__conn-tile--github" style={{ width: 38, height: 38 }}><GithubIcon size={18} /></span>
                <div className="tpo__grow-text">
                  <span className="tpo__grow-label">Signal sources</span>
                  <span className="tpo__grow-value">
                    {t.selectedJiraKeys.length} Jira project{t.selectedJiraKeys.length === 1 ? '' : 's'}
                    {t.githubConnected ? `, ${t.selectedRepoNames.length} repo${t.selectedRepoNames.length === 1 ? '' : 's'}` : ''}, plus Slack
                  </span>
                </div>
              </div>
              <div className="tpo__grow">
                <div className="tpo__grow-text">
                  <span className="tpo__grow-label">Standup</span>
                  <span className="tpo__grow-value">
                    {t.standupEnabled
                      ? `Weekdays at ${t.standupTime}${t.timezone ? `, ${t.timezone}` : ''}${t.selectedChannel ? ` → #${t.selectedChannel.name}` : ''}${t.reviewBeforePost ? ' · review before posting' : ''}`
                      : 'Off — set up later'}
                  </span>
                </div>
              </div>
            </div>

            <div className="tpo__callout tpo__callout--blue">
              <InfoIcon />
              <span>
                {t.standupEnabled
                  ? `When the standup runs, each person${t.reviewBeforePost ? ' sees their line and can edit or approve it' : ' has their line posted automatically'}. The per-person update text is generated by the standup engine once it runs.`
                  : `You're all set. Open the team's settings whenever you want to turn the automated standup on.`}
              </span>
            </div>
          </>
        );

      default:
        return null;
    }
  }
};

export default OnboardingWizard;
